import { isBrowser } from "@utils/browser";
import classNames from "classnames";
import { Component, InfernoNode } from "inferno";

interface TabItem {
  key: string;
  getNode: (isSelected: boolean) => InfernoNode;
  label: string;
}

interface TabsProps {
  tabs: TabItem[];
  /** Opens this tab on first render when the key exists. */
  defaultTab?: string;
  /** Sync active tab with `location.hash` (e.g. `#auth`). */
  syncHash?: boolean;
}

interface TabsState {
  currentTab: string;
}

function resolveInitialTab(props: TabsProps): string {
  const keys = props.tabs.map(t => t.key);
  if (props.defaultTab && keys.includes(props.defaultTab)) {
    return props.defaultTab;
  }
  if (props.syncHash && isBrowser()) {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash && keys.includes(hash)) {
      return hash;
    }
  }
  return props.tabs[0]?.key ?? "";
}

function handleSwitchTab({ ctx, tab }: { ctx: Tabs; tab: string }) {
  ctx.setState({ currentTab: tab });
  if (ctx.props.syncHash && isBrowser()) {
    const url = new URL(window.location.href);
    url.hash = tab;
    window.history.replaceState(null, "", url);
  }
}

export default class Tabs extends Component<TabsProps, TabsState> {
  constructor(props: TabsProps, context: object) {
    super(props, context);

    this.state = {
      currentTab: resolveInitialTab(props),
    };
  }

  componentDidUpdate(prevProps: Readonly<TabsProps>) {
    if (
      prevProps.defaultTab !== this.props.defaultTab &&
      this.props.defaultTab &&
      this.props.tabs.some(t => t.key === this.props.defaultTab)
    ) {
      this.setState({ currentTab: this.props.defaultTab });
    }
  }

  render() {
    return (
      <div>
        <div className="tabs-scroll overflow-x-auto">
          <ul className="nav nav-tabs mb-2 flex-nowrap" role="tablist">
            {this.props.tabs.map(({ key, label }) => (
              <li key={key} className="nav-item">
                <button
                  type="button"
                  className={classNames("nav-link text-nowrap", {
                    active: this.state?.currentTab === key,
                  })}
                  onClick={() => handleSwitchTab({ ctx: this, tab: key })}
                  aria-controls={`${key}-tab-pane`}
                  {...(this.state?.currentTab === key && {
                    ...{
                      "aria-current": "page",
                      "aria-selected": "true",
                    },
                  })}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="tab-content">
          {this.props.tabs.map(({ key, getNode }) => {
            return getNode(this.state?.currentTab === key);
          })}
        </div>
      </div>
    );
  }
}
